import { ApiResponse, ProjectTemplate } from '@/types';

export const templateService = {
  // Get all project templates
  getTemplates: async (params?: {
    category?: string;
    search?: string;
  }): Promise<ApiResponse<ProjectTemplate[]>> => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.category) queryParams.append('category', params.category);
      if (params?.search) queryParams.append('search', params.search);

      // Mock data - TODO: Implement actual API call

      // For now, return mock data since backend isn't implemented yet
      return {
        success: true,
        data: [
          {
            id: '1',
            name: 'Residential Construction',
            description: 'Standard template for residential building projects',
            category: 'Residential',
            tools: [
              {
                id: 'drawings',
                name: 'Drawing Management',
                description: 'Store and version control drawings',
                category: 'design',
                isRequired: true,
              },
              {
                id: 'document-control',
                name: 'Document Control',
                description: 'Centralized document management',
                category: 'management',
                isRequired: true,
              },
              {
                id: 'gantt',
                name: 'Gantt Charts',
                description: 'Visual project timeline and task management',
                category: 'planning',
                isRequired: false,
              },
              {
                id: 'progress-photos',
                name: 'Progress Photos',
                description: 'Visual progress documentation',
                category: 'construction',
                isRequired: false,
              },
            ],
            customFields: [
              {
                id: 'units',
                name: 'units',
                type: 'number',
                label: 'Number of Units',
                required: true,
                defaultValue: 1,
              },
              {
                id: 'stories',
                name: 'stories',
                type: 'number',
                label: 'Number of Stories',
                required: true,
                defaultValue: 1,
              },
              {
                id: 'building_type',
                name: 'building_type',
                type: 'dropdown',
                label: 'Building Type',
                required: true,
                options: ['Single Family', 'Multi Family', 'Townhouse', 'Condo'],
                defaultValue: 'Single Family',
              },
            ],
            directories: [
              {
                id: 'documents',
                name: 'Documents',
                description: 'General project documents',
                tags: ['general'],
                isDefault: true,
              },
              {
                id: 'contracts',
                name: 'Contracts',
                description: 'Legal documents and agreements',
                tags: ['legal', 'admin'],
                isDefault: true,
              },
              {
                id: 'permits',
                name: 'Permits & Licenses',
                description: 'Building permits and licenses',
                tags: ['legal', 'compliance'],
                isDefault: true,
              },
              {
                id: 'drawings',
                name: 'Drawings',
                description: 'Architectural and engineering drawings',
                tags: ['design', 'technical'],
                isDefault: false,
              },
              {
                id: 'inspections',
                name: 'Inspections',
                description: 'Quality control inspections',
                tags: ['quality', 'compliance'],
                isDefault: false,
              },
            ],
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
          {
            id: '2',
            name: 'Commercial Building',
            description: 'Template for commercial construction projects',
            category: 'Commercial',
            tools: [
              {
                id: 'drawings',
                name: 'Drawing Management',
                description: 'Store and version control drawings',
                category: 'design',
                isRequired: true,
              },
              {
                id: 'document-control',
                name: 'Document Control',
                description: 'Centralized document management',
                category: 'management',
                isRequired: true,
              },
              {
                id: 'bim',
                name: 'BIM Support',
                description: 'Building Information Modeling',
                category: 'design',
                isRequired: false,
              },
              {
                id: 'budget-tracking',
                name: 'Budget Tracking',
                description: 'Monitor project costs and expenses',
                category: 'management',
                isRequired: false,
              },
              {
                id: 'change-orders',
                name: 'Change Orders',
                description: 'Manage project modifications',
                category: 'management',
                isRequired: false,
              },
              {
                id: 'safety-inspections',
                name: 'Safety Inspections',
                description: 'Schedule and track safety checks',
                category: 'safety',
                isRequired: false,
              },
            ],
            customFields: [
              {
                id: 'floor_area',
                name: 'floor_area',
                type: 'number',
                label: 'Total Floor Area (sq ft)',
                required: true,
                defaultValue: 10000,
              },
              {
                id: 'occupancy_type',
                name: 'occupancy_type',
                type: 'dropdown',
                label: 'Occupancy Type',
                required: true,
                options: ['Office', 'Retail', 'Restaurant', 'Warehouse', 'Mixed Use'],
                defaultValue: 'Office',
              },
              {
                id: 'parking_spaces',
                name: 'parking_spaces',
                type: 'number',
                label: 'Parking Spaces',
                required: false,
                defaultValue: 50,
              },
            ],
            directories: [
              {
                id: 'documents',
                name: 'Documents',
                description: 'General project documents',
                tags: ['general'],
                isDefault: true,
              },
              {
                id: 'contracts',
                name: 'Contracts',
                description: 'Legal documents and agreements',
                tags: ['legal', 'admin'],
                isDefault: true,
              },
              {
                id: 'permits',
                name: 'Permits & Licenses',
                description: 'Building permits and licenses',
                tags: ['legal', 'compliance'],
                isDefault: true,
              },
              {
                id: 'drawings',
                name: 'Drawings',
                description: 'Architectural and engineering drawings',
                tags: ['design', 'technical'],
                isDefault: false,
              },
              {
                id: 'bim-models',
                name: 'BIM Models',
                description: '3D building information models',
                tags: ['design', 'bim'],
                isDefault: false,
              },
              {
                id: 'inspections',
                name: 'Inspections',
                description: 'Quality control inspections',
                tags: ['quality', 'compliance'],
                isDefault: false,
              },
              {
                id: 'safety-reports',
                name: 'Safety Reports',
                description: 'Safety incidents and reports',
                tags: ['safety', 'compliance'],
                isDefault: false,
              },
            ],
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
          {
            id: '3',
            name: 'Infrastructure Project',
            description: 'Template for infrastructure and civil projects',
            category: 'Infrastructure',
            tools: [
              {
                id: 'drawings',
                name: 'Drawing Management',
                description: 'Store and version control drawings',
                category: 'design',
                isRequired: true,
              },
              {
                id: 'document-control',
                name: 'Document Control',
                description: 'Centralized document management',
                category: 'management',
                isRequired: true,
              },
              {
                id: 'daily-logs',
                name: 'Daily Logs',
                description: 'Track daily progress and activities',
                category: 'construction',
                isRequired: false,
              },
              {
                id: 'quality-control',
                name: 'Quality Control',
                description: 'Quality assurance and testing',
                category: 'quality',
                isRequired: false,
              },
              {
                id: 'safety-inspections',
                name: 'Safety Inspections',
                description: 'Schedule and track safety checks',
                category: 'safety',
                isRequired: false,
              },
            ],
            customFields: [
              {
                id: 'project_length',
                name: 'project_length',
                type: 'number',
                label: 'Project Length (miles)',
                required: false,
                defaultValue: 1,
              },
              {
                id: 'infrastructure_type',
                name: 'infrastructure_type',
                type: 'dropdown',
                label: 'Infrastructure Type',
                required: true,
                options: ['Road', 'Bridge', 'Water/Sewer', 'Utilities', 'Parks'],
                defaultValue: 'Road',
              },
            ],
            directories: [
              {
                id: 'documents',
                name: 'Documents',
                description: 'General project documents',
                tags: ['general'],
                isDefault: true,
              },
              {
                id: 'contracts',
                name: 'Contracts',
                description: 'Legal documents and agreements',
                tags: ['legal', 'admin'],
                isDefault: true,
              },
              {
                id: 'permits',
                name: 'Permits & Licenses',
                description: 'Building permits and licenses',
                tags: ['legal', 'compliance'],
                isDefault: true,
              },
              {
                id: 'drawings',
                name: 'Drawings',
                description: 'Civil engineering drawings',
                tags: ['design', 'technical'],
                isDefault: false,
              },
              {
                id: 'daily-reports',
                name: 'Daily Reports',
                description: 'Daily construction logs',
                tags: ['construction', 'progress'],
                isDefault: false,
              },
              {
                id: 'material-delivery',
                name: 'Material Delivery',
                description: 'Delivery receipts and logs',
                tags: ['construction', 'materials'],
                isDefault: false,
              },
              {
                id: 'inspections',
                name: 'Inspections',
                description: 'Quality control inspections',
                tags: ['quality', 'compliance'],
                isDefault: false,
              },
            ],
            isActive: true,
            createdAt: new Date(),
            updatedAt: new Date(),
          },
        ],
      };

      // Uncomment when backend is ready:
      // return await apiService.get<ProjectTemplate[]>(url);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch templates',
      };
    }
  },

  // Get template by ID
  getTemplate: async (id: string): Promise<ApiResponse<ProjectTemplate>> => {
    try {
      // For now, return mock data
      const templates = await templateService.getTemplates();
      if (templates.success) {
        const template = templates.data?.find(t => t.id === id);
        if (template) {
          return {
            success: true,
            data: template,
          };
        }
      }

      return {
        success: false,
        message: 'Template not found',
      };

      // Uncomment when backend is ready:
      // return await apiService.get<ProjectTemplate>(`/templates/${id}`);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch template',
      };
    }
  },

  // Create new template (admin only)
  createTemplate: async (
    templateData: Omit<ProjectTemplate, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<ApiResponse<ProjectTemplate>> => {
    try {
      // For now, return mock success
      return {
        success: true,
        data: {
          ...templateData,
          id: `template-${Date.now()}`,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      };

      // Uncomment when backend is ready:
      // return await apiService.post<ProjectTemplate>('/templates', templateData);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create template',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Update template (admin only)
  updateTemplate: async (
    id: string,
    templateData: Partial<ProjectTemplate>
  ): Promise<ApiResponse<ProjectTemplate>> => {
    try {
      // For now, return mock success
      const existing = await templateService.getTemplate(id);
      if (existing.success && existing.data) {
        return {
          success: true,
          data: {
            ...existing.data,
            ...templateData,
            updatedAt: new Date(),
          },
        };
      }

      return {
        success: false,
        message: 'Template not found',
      };

      // Uncomment when backend is ready:
      // return await apiService.put<ProjectTemplate>(`/templates/${id}`, templateData);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update template',
        errors: error.response?.data?.errors,
      };
    }
  },

  // Delete template (admin only)
  deleteTemplate: async (): Promise<ApiResponse<void>> => {
    // For now, return mock success
    // TODO: Uncomment when backend is ready:
    // try {
    //   return await apiService.delete<void>(`/templates/${id}`);
    // } catch (error: any) {
    //   return {
    //     success: false,
    //     message: error.response?.data?.message || 'Failed to delete template',
    //   };
    // }
    return {
      success: true,
    };
  },
};
